import { useUserStore } from '@/store/userStore';
import { useClassData } from './hooks/useClassData';
import { useBookingDetails } from './hooks/useBookingDetails';
import { useBookingAttendees } from './hooks/useBookingAttendees';
import { useBookingConfirmation, type BookingConfirmationOutcome } from './hooks/useBookingConfirmation';
import { scheduleClassesService } from '@/services/scheduleClassesService';
import { useBookingsSupabaseStore } from '@/store/api/bookingsSupabaseStore';
import { format, isValid } from 'date-fns';
import { useSuccessSound } from '@/hooks/useSuccessSound';
import { toast } from 'sonner';
import i18n from '@/i18n';

/**
 * Main hook for managing class bookings with store integration
 */
export const useClassBookingWithStore = (
  isOpen: boolean,
  classId: string,
  dateString: string | undefined,
  onOpenChange: (open: boolean) => void,
  onConfirm?: () => void,
  classes: any[] = [],
  addRecentlyBookedSlot?: (classId: string, date: string, time: string) => void
) => {
  // Get user role
  const { role } = useUserStore();
  const isAdmin = role === 'admin';
  const isCoach = role === 'coach';

  // Hook pour le son de succès
  const { playSuccessSound } = useSuccessSound();

  // Use custom hooks for different concerns
  const { selectedClass, refreshClassData } = useClassData(classId, classes);

  const {
    selectedDate,
    selectedTime,
    handleTimeChange,
    // Places restantes figées à l'ouverture (capacité - présences serveur).
    // Seule valeur disponible côté adhérent, qui ne coche personne.
    availableSlots: initialAvailableSlots,
    setAvailableSlots,
    handleSlotsChange,
    totalCapacity,
    setTotalCapacity
  } = useBookingDetails(selectedClass, dateString);

  const {
    searchQuery,
    setSearchQuery,
    filteredAttendees,
    allAttendees,
    handleAttendeeToggle,
    initialSelectedAttendees,
    existingBookings,
    // Valeur VIVANTE : capacité - participants cochés - séances d'essai.
    // Elle était calculée puis jamais lue ; l'UI se basait sur la valeur figée,
    // d'où sur-réservation possible et cases restant grisées après décochage.
    availableSlots: liveAvailableSlots,
    isLoadingClients,
    isFullyLoaded,
    loadError,
    retryLoad,
    fetchExistingBookings,
    eligibilityUnavailable,
  } = useBookingAttendees(isAdmin, isCoach, isOpen, classId, dateString, totalCapacity);

  // L'adhérent n'a pas de liste de participants : on garde la valeur serveur.
  const availableSlots = (isAdmin || isCoach) ? liveAvailableSlots : initialAvailableSlots;

  const { confirmBooking: confirmBookingHandler } = useBookingConfirmation(
    classId,
    selectedClass,
    selectedDate,
    selectedTime,
    onOpenChange,
    onConfirm
  );

  /**
   * Execute the booking after the user has confirmed in the impact dialog.
   * Performs a last-mile refresh and aborts if the server-side state has
   * drifted from what was shown to the user.
   */
  const confirmBooking = async (): Promise<BookingConfirmationOutcome> => {
    try {
      // Last-mile refresh: re-read the truth from the DB right before mutating.
      const fresh = await fetchExistingBookings().catch(() => null);
      if (fresh) {
        const freshActive = new Set(
          fresh.filter(b => b.status !== 'cancelled').map(b => b.userId)
        );
        const initial = new Set(initialSelectedAttendees);
        const sameSize = freshActive.size === initial.size;
        const sameMembers = sameSize && Array.from(freshActive).every((id) => initial.has(id as string));
        if (!sameMembers) {
          toast.error(
            i18n.t('booking.participantsChanged', { ns: 'bookingui' }),
            { duration: 6000 }
          );
          return { confirmed: 0, failed: 1, removed: 0 };
        }
      }

      // ⚠️ IMPORTANT : on passe `allAttendees` (liste complète non filtrée par
      // la recherche), sinon les adhérents déjà cochés mais hors filtre seraient
      // considérés comme désélectionnés et leurs bookings seraient supprimés.
      const outcome = await confirmBookingHandler(
        isAdmin || isCoach,
        allAttendees,
        initialSelectedAttendees,
        existingBookings
      );

      // Son et « slot récemment réservé » sont des signaux de succès : ils
      // s'exécutaient même après un échec métier (« plus de séances
      // disponibles »), donnant à l'utilisateur la certitude d'être inscrit.
      if (outcome.confirmed > 0) {
        playSuccessSound();

        // `isValid` : une date invalide ferait lever `format` (date-fns v3)
        // juste après une réservation réussie.
        if (addRecentlyBookedSlot && selectedClass && isValid(selectedDate) && selectedTime) {
          const bookedAttendees = allAttendees.filter(a => a.selected && !initialSelectedAttendees.has(a.id));
          bookedAttendees.forEach(() => {
            addRecentlyBookedSlot(selectedClass.id, format(selectedDate, 'yyyy-MM-dd'), selectedTime);
          });
        }
      }

      const { user, gymId } = useUserStore.getState();
      if (user?.id && gymId) {
        scheduleClassesService.invalidateCache(user.id, gymId);
      }

      Promise.all([
        useBookingsSupabaseStore.getState().fetchBookings(),
        refreshClassData(),
        onConfirm?.()
      ]).then(() => {
        console.log('[confirmBooking] Background refreshes completed');
      }).catch(err => {
        console.error('[confirmBooking] Background refresh error:', err);
      });

      return outcome;
    } catch (error) {
      console.error("Error in confirmBooking:", error);
      return { confirmed: 0, failed: 1, removed: 0 };
    }
  };

  return {
    selectedClass,
    selectedDate,
    selectedTime,
    handleTimeChange,
    availableSlots,
    handleSlotsChange,
    totalCapacity,
    searchQuery,
    setSearchQuery,
    filteredAttendees,
    allAttendees,
    handleAttendeeToggle,
    confirmBooking,
    refreshClassData,
    isLoadingClients,
    initialSelectedAttendees,
    isFullyLoaded,
    loadError,
    retryLoad,
    eligibilityUnavailable,
  };
};
